import React, { createContext, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useUserStore } from "../stores/userStore";

const CannyContext = createContext();

/**
 * CannyProvider — loads Canny SDK and identifies the user when authenticated
 * so links with `data-canny-link` attribute recognize the user.
 * @see https://developers.canny.io/install
 */
export const CannyProvider = ({ children, useCanny }) => {
  const { isAuthenticated } = useAuth();
  const user = useUserStore((state) => state.user);

  const defaultCanny = () => ({
    isEnabled: false,
    identify: () => {},
    reset: () => {},
  });
  const useCannyHook = useCanny || defaultCanny;
  const { identify, reset, isEnabled } = useCannyHook();

  useEffect(() => {
    if (isAuthenticated && user && isEnabled) {
      identify({
        id: user.id,
        email: user.email,
        name:
          user.name || `${user.firstname || ""} ${user.lastname || ""}`.trim(),
        created: user.created_at,
        ...(user.photo && { avatarURL: user.photo }),
      });
    } else if (!isAuthenticated && isEnabled) {
      reset();
    }
  }, [isAuthenticated, user, isEnabled, identify, reset]);

  const value = {
    identify,
    reset,
    isEnabled,
  };

  return (
    <CannyContext.Provider value={value}>{children}</CannyContext.Provider>
  );
};

export const useCannyContext = () => {
  const context = useContext(CannyContext);
  if (!context) {
    throw new Error("useCannyContext must be used within a CannyProvider");
  }
  return context;
};

export default CannyContext;
